import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  ApiError,
  SURVEY_CATEGORIES,
  SURVEY_CATEGORY_LABEL,
  api,
  canEdit,
  type AuthUser,
  type Survey,
  type SurveyCategory,
} from '@/lib/api';

export default function Surveys({ user }: { user: AuthUser }) {
  const editable = canEdit(user.role);
  const [creating, setCreating] = useState(false);

  const surveysQuery = useQuery({
    queryKey: ['surveys'],
    queryFn: api.listSurveys,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Surveys</h1>
          <p className="text-sm text-slate-400">
            Track yes/no responses from the alliance on any prompt.
          </p>
        </div>
        {editable && !creating && (
          <button
            className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} /> New survey
          </button>
        )}
      </header>

      {creating && editable && (
        <CreateSurveyDialog onClose={() => setCreating(false)} />
      )}

      {surveysQuery.isLoading && (
        <div className="text-slate-400">Loading…</div>
      )}
      {surveysQuery.data && surveysQuery.data.length === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
          No surveys yet.
        </div>
      )}
      <ul className="space-y-2">
        {surveysQuery.data?.map((s) => (
          <SurveyCard key={s.id} survey={s} editable={editable} />
        ))}
      </ul>

      <Style />
    </div>
  );
}

function SurveyCard({ survey, editable }: { survey: Survey; editable: boolean }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.deleteSurvey(survey.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
  const { yes, no, pending, total } = survey.counts;
  const answered = yes + no;
  const pct = total > 0 ? (answered / total) * 100 : 0;

  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link
            to={`/surveys/${survey.id}`}
            className="text-base font-medium hover:text-indigo-300"
          >
            {survey.title}
          </Link>
          {survey.description && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-400">
              {survey.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Created {survey.createdAt.slice(0, 10)}</span>
            <span
              className={
                survey.status === 'open'
                  ? 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
                  : 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
              }
            >
              {survey.status}
            </span>
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">
              {SURVEY_CATEGORY_LABEL[survey.category]}
            </span>
            <span className="tabular-nums">
              {yes} yes · {no} no · {pending} pending
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-slate-800">
            <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {editable && (
          <button
            className="text-slate-500 hover:text-red-400"
            title="Delete survey"
            onClick={() => {
              if (confirm(`Delete survey "${survey.title}"?`)) remove.mutate();
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </li>
  );
}

function CreateSurveyDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<SurveyCategory>('general');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createSurvey({
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        category,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (!title.trim()) return;
        create.mutate();
      }}
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3"
    >
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
          className="input"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as SurveyCategory)}
          className="input"
        >
          {SURVEY_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {SURVEY_CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Description (optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="input"
        />
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
